import { getConsentSnapshot } from '@/contexts/consent/store';
import { waitForInitialContent } from '@/lib/initial-content';

type ClarityApi = Pick<(typeof import('@microsoft/clarity'))['default'], 'init' | 'consentV2'>;
type ClarityCommand = ((command: 'start' | 'stop', config?: Record<string, never>) => void) & { q?: unknown[] };
type ClarityWindow = Window & { clarity?: ClarityCommand };

/** 只删除当前站点可访问的 Clarity 第一方 Cookie，不改其它 Cookie 或第三方存储。 */
function clearClarityCookies(): void {
    const parts = window.location.hostname.split('.');
    const domains = ['', ...parts.map((_, index) => parts.slice(index).join('.')).filter((domain) => domain.includes('.'))];
    for (const name of ['_clck', '_clsk']) {
        for (const domain of domains) {
            document.cookie = `${name}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ''}`;
        }
    }
}

export function createClarityClient(
    projectId: string,
    load: () => Promise<ClarityApi> = () => import('@microsoft/clarity').then((module) => module.default),
    beforeLoad?: () => Promise<void>,
) {
    let sdk: ClarityApi | null = null;
    let loading: Promise<void> | null = null;
    let initialized = false;
    let running = false;
    let active = false;
    let generation = 0;
    let stopRequested = false;
    let installedScript: HTMLElement | null = null;

    const allowed = () => active && Boolean(projectId) && getConsentSnapshot().analyticsAllowed;

    function command(name: 'start' | 'stop'): boolean {
        try {
            const api = (window as ClarityWindow).clarity;
            if (!api) return false;
            // 非空配置避免 SDK 再次处理已安装的命令队列；空对象不覆盖原项目配置。
            if (name === 'start') api(name, {});
            else api(name);
            return true;
        } catch {
            console.warn(`[clarity] ${name} failed`);
            return false;
        }
    }

    function stop(): void {
        generation += 1;
        if (initialized && running) {
            // 不调用 consentV2 denied：它会自行重启有限采集。stop 可发送用户允许的授权期末包。
            stopRequested = true;
            if (command('stop')) { running = false; stopRequested = false; }
        }
        if (initialized && typeof window !== 'undefined') clearClarityCookies();
    }

    function start(): void {
        if (!allowed() || !sdk) return;
        try {
            if (!initialized) {
                sdk.init(projectId);
                // npm 包会吞掉 script 注入异常，不能仅凭 init 返回就当作已成功。
                if (!document.getElementById('clarity-script') || !(window as ClarityWindow).clarity) {
                    console.warn('[clarity] script was not installed');
                    return;
                }
                initialized = true;
                running = true;
                installedScript = document.getElementById('clarity-script');
                const script = installedScript!;
                script.addEventListener('error', () => {
                    if (installedScript !== script) return;
                    initialized = false;
                    running = false;
                    installedScript = null;
                    script.remove();
                    const api = (window as ClarityWindow).clarity;
                    if (api?.q) api.q.length = 0;
                    console.warn('[clarity] script load failed');
                }, { once: true });
            } else if (!running) {
                if (!command('start')) return;
                running = true;
            }
            sdk.consentV2({ ad_Storage: 'denied', analytics_Storage: 'granted' });
        } catch {
            console.warn('[clarity] activation failed');
        }
    }

    function initialize(): Promise<void> {
        if (typeof window === 'undefined' || !allowed()) return Promise.resolve();
        if (stopRequested) { if (command('stop')) { running = false; stopRequested = false; } else return Promise.resolve(); }
        if (sdk) { start(); return Promise.resolve(); }
        if (loading) return loading;
        const epoch = generation;
        const request = (beforeLoad?.() ?? Promise.resolve())
            .then(() => allowed() && epoch === generation ? load() : null)
            .then((module) => {
                if (!module) return;
                sdk = module;
                if (epoch === generation && allowed()) start();
            })
            .catch(() => console.warn('[clarity] module load failed'))
            .finally(() => {
                if (loading === request) loading = null;
                if (allowed() && epoch !== generation) void initialize();
            });
        loading = request;
        return request;
    }

    return {
        start(): Promise<void> { active = true; return initialize(); },
        syncConsent(): Promise<void> {
            if (!allowed()) { stop(); return Promise.resolve(); }
            return initialize();
        },
        resetIdentity(): void {
            stop();
            if (allowed()) void initialize();
        },
        dispose(): void { active = false; stop(); },
    };
}

export const clarityClient = createClarityClient(
    process.env.NEXT_PUBLIC_MICROSOFT_CLARITY_PROJECT_ID?.trim() ?? '',
    undefined,
    waitForInitialContent,
);
